/**
 * Operators: schemas, list search, queries and mutations (GET/POST/PATCH/DELETE /api/operators).
 *
 * Secrets: every operator response carries `ApiSalt` (the key the operator's wallet calls are
 * signed with). The schemas below don't declare it, so Zod strips it and it never reaches the query
 * cache; `loadOperatorSalt` fetches it on an explicit reveal only. The server also generates the
 * UUID on create, and the UUID must never change afterwards (rgs-domain), so it's never sent.
 */
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { http } from '@/api/http';
import {
  encodePagedQuery,
  pagedSchema,
  type FilterDescriptor,
  type PagedQuery,
  type SortDescriptor,
} from '@/api/paged-query';
import { flag, hexId } from '@/api/zod-helpers';
import type { FieldMeta } from '@/lib/diff';
import { filterParam, listSearchSchema, toPagedQuery } from '@/lib/url-state';

/** 1 = debit and credit in one wallet call, 2 = separate calls (rgs-domain). */
export const TRANSACTION_TYPES = ['1', '2'] as const;
export type TransactionType = (typeof TRANSACTION_TYPES)[number];
export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  '1': 'Single call (debit + credit)',
  '2': 'Separate debit and credit calls',
};
export const transactionTypeLabel = (type: number) => TRANSACTION_TYPE_LABELS[String(type)] ?? `Type ${type}`;

/** The /operators responses serialise dates as `{}` (a backend bug); anything that isn't a string is unknown. */
const timestamp = z.unknown().transform((v) => (typeof v === 'string' && v ? v : null));

export const operatorSchema = z.object({
  Id: z.coerce.number().int(),
  UUID: hexId.nullish().transform((v) => v ?? null),
  Name: z.string(),
  /** The operator's wallet endpoint. */
  ApiURL: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  TransactionsType: z.coerce.number().int(),
  /** Only active operators are served by the game API. */
  Status: flag,
  /** Wallet call timeout, seconds. */
  Timeout: z.coerce.number(),
  CreatedAt: timestamp,
  UpdatedAt: timestamp,
});

export type Operator = z.infer<typeof operatorSchema>;

// ---- list -------------------------------------------------------------------------------------

/** The server's sortable allow-list (module-fields.config.ts, `operators`). */
export const OPERATOR_SORT_FIELDS = ['Id', 'Name', 'Status', 'CreatedAt', 'UpdatedAt'] as const;

export const DEFAULT_OPERATOR_SORT: SortDescriptor = { field: 'Id', dir: 'desc' };

export const operatorsSearchSchema = listSearchSchema({
  sortFields: OPERATOR_SORT_FIELDS,
  filters: {
    active: filterParam.boolean(),
    transactions: filterParam.enum(TRANSACTION_TYPES),
  },
});

export type OperatorsSearch = z.infer<typeof operatorsSearchSchema>;

export function operatorsPagedQuery(search: OperatorsSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  if (search.q) filters.push({ field: 'Name', operator: 'contains', value: search.q });
  // Status is 1 enabled, 2 disabled (rgs-domain); `neq 1` also catches any stray 0.
  if (search.active !== undefined) filters.push({ field: 'Status', operator: search.active ? 'eq' : 'neq', value: 1 });
  if (search.transactions)
    filters.push({ field: 'TransactionsType', operator: 'eq', value: Number(search.transactions) });
  return toPagedQuery(search, { defaultSort: DEFAULT_OPERATOR_SORT, filters });
}

const listSchema = pagedSchema(operatorSchema);

export const operatorsKeys = {
  all: ['operators'] as const,
  list: (query: PagedQuery) => ['operators', 'list', query] as const,
  detail: (id: number) => ['operators', 'detail', id] as const,
  brands: (id: number) => ['operators', 'brands', id] as const,
};

export const operatorsListQuery = (search: OperatorsSearch) => {
  const query = operatorsPagedQuery(search);
  return queryOptions({
    queryKey: operatorsKeys.list(query),
    queryFn: ({ signal }) => http('/operators', { query: encodePagedQuery(query), signal }, listSchema),
    placeholderData: keepPreviousData,
  });
};

export const operatorQuery = (id: number) =>
  queryOptions({
    queryKey: operatorsKeys.detail(id),
    queryFn: ({ signal }) => http(`/operators/${id}`, { signal }, operatorSchema),
  });

/** The salt, fetched fresh for one reveal and never cached. */
export async function loadOperatorSalt(id: number): Promise<string> {
  const { ApiSalt } = await http(`/operators/${id}`, {}, z.object({ ApiSalt: z.string().nullish() }));
  return ApiSalt ?? '';
}

// ---- the operator's brands --------------------------------------------------------------------

/** The brands API can't filter by operator (its allow-list lacks `operatorId`), so this filters here. */
export const OPERATOR_BRANDS_LIMIT = 500;

const operatorBrandSchema = z.object({
  Id: z.coerce.number().int(),
  operatorId: z.coerce.number().int().nullish(),
  ExternalBrandId: z.string(),
  Name: z.string(),
  Status: flag,
});

export type OperatorBrand = z.infer<typeof operatorBrandSchema>;

export const operatorBrandsQuery = (operatorId: number) =>
  queryOptions({
    queryKey: operatorsKeys.brands(operatorId),
    queryFn: async ({ signal }) => {
      const page = await http(
        '/brands',
        {
          query: encodePagedQuery({ skip: 0, take: OPERATOR_BRANDS_LIMIT, sort: { field: 'Name', dir: 'asc' } }),
          signal,
        },
        pagedSchema(operatorBrandSchema),
      );
      return {
        brands: page.data.filter((b) => b.operatorId === operatorId),
        truncated: page.total > page.data.length,
      };
    },
  });

// ---- writes -----------------------------------------------------------------------------------

/** What the edit form holds. `ApiSalt` is a new salt to set, or '' to keep the current one. */
export interface OperatorValues extends Record<string, unknown> {
  Name: string;
  ApiURL: string;
  TransactionsType: TransactionType;
  Timeout: number;
  Status: boolean;
  ApiSalt: string;
}

export const OPERATOR_FIELDS: FieldMeta[] = [
  { key: 'Name', label: 'Name' },
  { key: 'ApiURL', label: 'Wallet URL' },
  { key: 'TransactionsType', label: 'Wallet calls', format: (v) => TRANSACTION_TYPE_LABELS[String(v)] ?? String(v) },
  { key: 'Timeout', label: 'Timeout (s)' },
  { key: 'Status', label: 'Status', format: (v) => (v ? 'Active' : 'Inactive') },
  { key: 'ApiSalt', label: 'API salt', secret: true },
];

const asTransactionType = (type: number): TransactionType => {
  const s = String(type);
  // An unknown type shows as 1 in the form; it's only sent if the user changes it (PATCH sends the diff).
  return (TRANSACTION_TYPES as readonly string[]).includes(s) ? (s as TransactionType) : '1';
};

export const toOperatorValues = (o: Operator): OperatorValues => ({
  Name: o.Name,
  ApiURL: o.ApiURL,
  TransactionsType: asTransactionType(o.TransactionsType),
  Timeout: o.Timeout,
  Status: o.Status,
  ApiSalt: '',
});

/**
 * Form values to the API body: numbers for type and status (1 enabled, 2 disabled), and no salt
 * unless one was entered.
 */
export function toWireBody(values: Partial<OperatorValues>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (key === 'ApiSalt') {
      if (typeof value === 'string' && value !== '') body.ApiSalt = value;
    } else if (key === 'Status') body.Status = value ? 1 : 2;
    else if (key === 'TransactionsType') body.TransactionsType = Number(value);
    else body[key] = value;
  }
  return body;
}

/** 16 random bytes as 32 hex characters, the format the existing salts use. */
export function generateSalt(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function useUpdateOperator(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      http(`/operators/${id}`, { method: 'PATCH', body: toWireBody(patch) }, operatorSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: operatorsKeys.all });
    },
  });
}

export function useCreateOperator() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: OperatorValues) =>
      http('/operators', { method: 'POST', body: toWireBody(values) }, operatorSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: operatorsKeys.all });
    },
  });
}

export function useDeleteOperator(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http(`/operators/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: operatorsKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: operatorsKeys.all });
    },
  });
}
